// Meetings.jsx — Employer side
import { useState, useEffect } from 'react'
import { get, post, patch, del } from '../lib/api'

const GCOLS = ['#4F46E5','#059669','#D97706','#7C3AED','#DB2777']
function gc(i) { return GCOLS[i % GCOLS.length] }

function fmtDate(d) {
  if (!d) return '—'
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric', year:'numeric' })
}
function fmtTime(tm) {
  if (!tm) return ''
  const [h, m] = tm.split(':'), hr = parseInt(h)
  return `${hr > 12 ? hr - 12 : hr || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`
}
function isPast(d) { return d && new Date(d) < new Date() }
function uid() { return Math.random().toString(36).slice(2) }

const STATUS_MAP = {
  scheduled:   { label:'Scheduled',   bg:'#EFF6FF', color:'#4F46E5', icon:'📅' },
  in_progress: { label:'In Progress', bg:'#FFFBEB', color:'#D97706', icon:'🗣️' },
  completed:   { label:'Completed',   bg:'#ECFDF5', color:'#059669', icon:'✅' },
}

function emptyItem() {
  return { id: uid(), title:'', assigned_to:'', due_date:'', completed: false }
}

// ════════════════════════════════════════════════════════════════════════════
// ROOT
// ════════════════════════════════════════════════════════════════════════════
export default function Meetings({ profile, t, presetCreateDate, onConsumePreset }) {
  const [view, setView]             = useState('list')
  const [meetings, setMeetings]     = useState([])
  const [employees, setEmployees]   = useState([])
  const [loading, setLoading]       = useState(true)
  const [groupBy, setGroupBy]       = useState('employee')
  const [filterType, setFilterType] = useState('all')

  // Opened from the Calendar "create meeting" action with a date prefilled
  useEffect(() => {
    if (presetCreateDate) setView('create')
  }, [presetCreateDate])
  const [search, setSearch]         = useState('')
  const [expandedId, setExpandedId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
  const [editingMeeting, setEditingMeeting] = useState(null)
  const [quickAdd, setQuickAdd]     = useState({})

  useEffect(() => { fetchAll() }, [])

  async function fetchAll() {
    setLoading(true)
    try {
      const [mtgs, emps] = await Promise.all([get('/meetings'), get('/users')])
      setMeetings(mtgs.meetings || [])
      setEmployees((emps.users || []).filter(u => u.role === 'employee'))
    } catch (e) { console.error(e.message) }
    setLoading(false)
  }

  async function deleteMeeting(id) {
    await del(`/meetings/${id}`)
    setDeletingId(null); setExpandedId(null); fetchAll()
  }

  async function setStatus(m, status) {
    await patch(`/meetings/${m.id}`, { status })
    fetchAll()
  }

  async function toggleItem(m, idx) {
    const items = [...(m.structured_action_items || [])]
    items[idx] = { ...items[idx], completed: !items[idx].completed }
    await patch(`/meetings/${m.id}`, { structured_action_items: items })
    fetchAll()
  }

  async function addQuickItem(m, itemData) {
    const items = [...(m.structured_action_items || []), { ...emptyItem(), ...itemData }]
    await patch(`/meetings/${m.id}`, { structured_action_items: items })
    setQuickAdd({})
    fetchAll()
  }

  async function removeItem(m, idx) {
    const items = (m.structured_action_items || []).filter((_, i) => i !== idx)
    await patch(`/meetings/${m.id}`, { structured_action_items: items })
    fetchAll()
  }

  if (view === 'create') return (
    <MeetingForm t={t} profile={profile} employees={employees} presetDate={presetCreateDate?.date} presetTime={presetCreateDate?.time}
      onDone={() => { fetchAll(); setView('list'); onConsumePreset && onConsumePreset() }}
      onCancel={() => { setView('list'); onConsumePreset && onConsumePreset() }} />
  )
  if (view === 'edit' && editingMeeting) return (
    <MeetingForm t={t} profile={profile} employees={employees}
      existing={editingMeeting}
      onDone={() => { fetchAll(); setView('list'); setEditingMeeting(null) }}
      onCancel={() => { setView('list'); setEditingMeeting(null) }} />
  )

  const empMap = Object.fromEntries(employees.map(e => [e.id, e]))

  function getAttendees(m) {
    if (m.meeting_type === 'group') return (m.attendee_ids || []).map(id => empMap[id]).filter(Boolean)
    return m.employee_id ? [empMap[m.employee_id]].filter(Boolean) : []
  }

  const filtered = meetings.filter(m => {
    if (filterType !== 'all' && m.meeting_type !== filterType) return false
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return (m.title || '').toLowerCase().includes(q) ||
      getAttendees(m).some(e => e.full_name.toLowerCase().includes(q))
  })

  function groupMeetings(list) {
    if (groupBy === 'type') return [
      { key:'individual', label:'👤 Individual Meetings', items: list.filter(m => m.meeting_type !== 'group') },
      { key:'group',      label:'👥 Group Meetings',      items: list.filter(m => m.meeting_type === 'group') },
    ].filter(g => g.items.length)

    if (groupBy === 'employee') {
      const map = {}
      list.forEach(m => {
        const att = getAttendees(m)
        if (!att.length) {
          map['__'] = map['__'] || { label:'Unassigned', items:[] }
          map['__'].items.push(m)
        } else att.forEach(e => {
          map[e.id] = map[e.id] || { label: e.full_name, items:[] }
          map[e.id].items.push(m)
        })
      })
      return Object.entries(map).map(([k,v]) => ({ key:k, ...v })).sort((a,b) => a.label.localeCompare(b.label))
    }
    return [{ key:'all', label:'All Meetings', items: list }]
  }

  const groups = groupMeetings(filtered)
  const totalPending = meetings.reduce((s,m) => s + (m.structured_action_items||[]).filter(i=>!i.completed).length, 0)

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.75rem' }}>
        <div>
          <h1 style={{ fontSize:24, fontWeight:800, color:t.text, margin:'0 0 4px', letterSpacing:'-0.5px' }}>Meetings</h1>
          <p style={{ fontSize:14, color:t.textMuted, margin:0 }}>
            {meetings.length} meeting{meetings.length!==1?'s':''}
            {totalPending>0 && ` · ${totalPending} action item${totalPending!==1?'s':''} pending`}
          </p>
        </div>
        <button onClick={() => setView('create')}
          style={{ padding:'10px 20px', background:'linear-gradient(135deg,#4F46E5,#6366F1)', border:'none', borderRadius:8, fontSize:14, fontWeight:600, color:'#fff', cursor:'pointer', fontFamily:'Inter,system-ui', boxShadow:'0 2px 8px rgba(79,70,229,0.3)' }}>
          + New Meeting
        </button>
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:'1.5rem', flexWrap:'wrap', alignItems:'center' }}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…"
          style={{ padding:'9px 14px', background:t.card, border:`1px solid ${t.border}`, borderRadius:8, fontSize:13, color:t.text, outline:'none', fontFamily:'Inter,system-ui', width:220 }} />
        <div style={{ display:'flex', gap:6 }}>
          {[['all','All'],['individual','👤 Individual'],['group','👥 Group']].map(([v,l]) => (
            <button key={v} onClick={()=>setFilterType(v)}
              style={{ padding:'7px 12px', borderRadius:99, fontSize:12, fontWeight:600, cursor:'pointer', border:`1px solid ${filterType===v?'#4F46E5':t.border}`, background:filterType===v?'#4F46E514':t.card, color:filterType===v?'#4F46E5':t.textSub, fontFamily:'Inter,system-ui' }}>
              {l}
            </button>
          ))}
        </div>
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:12, color:t.textMuted }}>Group by:</span>
          <div style={{ display:'flex', gap:5 }}>
            {[['employee','Employee'],['type','Type'],['date','Date']].map(([v,l]) => (
              <button key={v} onClick={()=>setGroupBy(v)}
                style={{ padding:'6px 11px', borderRadius:7, fontSize:12, fontWeight:600, cursor:'pointer', border:`1px solid ${groupBy===v?'#4F46E5':t.border}`, background:groupBy===v?'#4F46E514':t.card, color:groupBy===v?'#4F46E5':t.textSub, fontFamily:'Inter,system-ui' }}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? <p style={{ color:t.textMuted }}>Loading…</p>
      : filtered.length===0 ? (
        <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'4rem', textAlign:'center', boxShadow:t.shadow }}>
          <div style={{ fontSize:48, marginBottom:12 }}>🗓</div>
          <h3 style={{ fontSize:18, fontWeight:700, color:t.text, marginBottom:8 }}>No meetings yet</h3>
          <button onClick={()=>setView('create')}
            style={{ padding:'12px 28px', background:'linear-gradient(135deg,#4F46E5,#6366F1)', border:'none', borderRadius:8, fontSize:14, fontWeight:600, color:'#fff', cursor:'pointer', fontFamily:'Inter,system-ui' }}>
            Schedule meeting →
          </button>
        </div>
      ) : groups.map(group => (
        <div key={group.key} style={{ marginBottom:'1.75rem' }}>
          {group.key !== 'all' && (
            <div style={{ fontSize:13, fontWeight:700, color:t.textMuted, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:10, display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:28, height:28, borderRadius:'50%', background:'linear-gradient(135deg,#4F46E5,#6366F1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:800, color:'#fff' }}>
                {group.label.replace(/[^a-zA-Z]/g,'').charAt(0).toUpperCase() || '?'}
              </div>
              {group.label}
              <span style={{ fontSize:11, fontWeight:600, background:'#4F46E514', color:'#4F46E5', padding:'2px 8px', borderRadius:99 }}>{group.items.length}</span>
            </div>
          )}

          {group.items.map(m => {
            const attendees  = getAttendees(m)
            const items      = m.structured_action_items || []
            const doneCount  = items.filter(i=>i.completed).length
            const progress   = items.length ? Math.round((doneCount/items.length)*100) : null
            const isExpanded = expandedId === m.id
            const isGroup    = m.meeting_type === 'group'
            const sm         = STATUS_MAP[m.status] || STATUS_MAP.scheduled
            const qa         = quickAdd[m.id]

            return (
              <div key={m.id} style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderLeft:'4px solid #4F46E5', borderRadius:14, marginBottom:20, overflow:'hidden', boxShadow:'0 2px 8px rgba(0,0,0,0.09)' }}>

                {deletingId === m.id && (
                  <div style={{ background:'#FEF2F2', borderBottom:'1px solid #FECACA', padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <span style={{ fontSize:13, fontWeight:600, color:'#DC2626' }}>⚠️ Delete this meeting and all its action items?</span>
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={()=>deleteMeeting(m.id)} style={{ padding:'6px 14px', background:'#EF4444', border:'none', borderRadius:7, fontSize:12, fontWeight:700, color:'#fff', cursor:'pointer' }}>Yes, delete</button>
                      <button onClick={()=>setDeletingId(null)} style={{ padding:'6px 14px', background:'transparent', border:'1px solid #FECACA', borderRadius:7, fontSize:12, fontWeight:600, color:'#DC2626', cursor:'pointer' }}>Cancel</button>
                    </div>
                  </div>
                )}

                <div style={{ padding:'1.25rem 1.5rem', cursor:'pointer', background:t.dark?'rgba(99,102,241,0.05)':'linear-gradient(135deg,#F8F9FF,#F4F4FF)' }}
                  onClick={()=>setExpandedId(isExpanded ? null : m.id)}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:19, fontWeight:800, color:t.text, marginBottom:5, letterSpacing:'-0.3px' }}>
                        {m.title || '1:1 Meeting'}
                      </div>
                      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                        <span style={{ fontSize:13, color:'#4F46E5', fontWeight:600 }}>
                          📅 {fmtDate(m.meeting_date)}
                          {m.meeting_time && ` · ${fmtTime(m.meeting_time)}`}
                          {m.meeting_end_time && ` – ${fmtTime(m.meeting_end_time)}`}
                        </span>
                        <span style={{ fontSize:11, fontWeight:700, padding:'2px 9px', borderRadius:99, background:isGroup?'#EFF6FF':'#F0F0FF', color:isGroup?'#3B82F6':'#4F46E5', border:`1px solid ${isGroup?'#BFDBFE':'#C7D2FE'}` }}>
                          {isGroup?'👥 Group':'👤 Individual'}
                        </span>
                        <span style={{ fontSize:11, fontWeight:700, padding:'2px 9px', borderRadius:99, background:sm.bg, color:sm.color }}>
                          {sm.icon} {sm.label}
                        </span>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:6, alignItems:'center', flexShrink:0, marginLeft:12 }} onClick={e=>e.stopPropagation()}>
                      {progress !== null && (
                        <div style={{ textAlign:'center', minWidth:48 }}>
                          <div style={{ fontSize:13, fontWeight:800, color:progress===100?'#059669':'#4F46E5' }}>{progress}%</div>
                          <div style={{ fontSize:10, color:t.textMuted }}>actions</div>
                        </div>
                      )}
                      <button onClick={()=>{ setEditingMeeting(m); setView('edit') }}
                        style={{ padding:'5px 10px', background:`${t.primary}14`, border:`1px solid ${t.primary}30`, borderRadius:7, fontSize:12, fontWeight:600, color:t.primary, cursor:'pointer', fontFamily:'Inter,system-ui' }}>
                        ✏️ Edit
                      </button>
                      <button onClick={()=>setDeletingId(m.id)}
                        style={{ width:30, height:30, background:'transparent', border:`1px solid ${t.border}`, borderRadius:7, color:t.textMuted, cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', justifyContent:'center' }}>
                        🗑
                      </button>
                      <span style={{ fontSize:13, color:t.textMuted }}>{isExpanded?'▲':'▼'}</span>
                    </div>
                  </div>

                  {attendees.length > 0 && (
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      {attendees.map((e,i) => (
                        <div key={e.id} style={{ display:'flex', alignItems:'center', gap:5, background:`${gc(i)}14`, border:`1px solid ${gc(i)}30`, borderRadius:99, padding:'3px 10px' }}>
                          <div style={{ width:16, height:16, borderRadius:'50%', background:gc(i), display:'flex', alignItems:'center', justifyContent:'center', fontSize:8, fontWeight:800, color:'#fff' }}>
                            {e.full_name?.charAt(0)}
                          </div>
                          <span style={{ fontSize:12, fontWeight:600, color:gc(i) }}>{e.full_name}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {items.length > 0 && (
                    <div style={{ marginTop:10 }}>
                      <div style={{ background:t.border, borderRadius:99, height:5, overflow:'hidden' }}>
                        <div style={{ width:`${progress}%`, height:'100%', background:progress===100?'#059669':'linear-gradient(90deg,#4F46E5,#6366F1)', borderRadius:99, transition:'width .4s' }} />
                      </div>
                      <div style={{ fontSize:11, color:t.textMuted, marginTop:3 }}>{doneCount}/{items.length} action items done</div>
                    </div>
                  )}
                </div>

                {isExpanded && (
                  <div style={{ padding:'1.25rem 1.5rem', borderTop:`1px solid ${t.border}` }}>

                    {/* RSVP */}
                    {attendees.length > 0 && (() => {
                      const rsvps = m.rsvp_statuses || {}
                      const RC = { accepted:'#059669', declined:'#EF4444', pending:'#D97706' }
                      return (
                        <div style={{ background:t.dark?'rgba(255,255,255,0.04)':'#F8F9FA', borderRadius:10, padding:'10px 14px', marginBottom:'1.25rem' }}>
                          <div style={{ fontSize:10, fontWeight:700, color:t.textMuted, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:8 }}>Employee Responses</div>
                          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                            {attendees.map((e,i) => {
                              const r = rsvps[e.id] || 'pending'
                              return (
                                <div key={e.id} style={{ display:'flex', alignItems:'center', gap:6, background:'#fff', border:`1px solid ${RC[r]}40`, borderRadius:99, padding:'4px 10px' }}>
                                  <div style={{ width:16, height:16, borderRadius:'50%', background:gc(i), display:'flex', alignItems:'center', justifyContent:'center', fontSize:8, fontWeight:800, color:'#fff' }}>{e.full_name?.charAt(0)}</div>
                                  <span style={{ fontSize:12, fontWeight:600, color:t.text }}>{e.full_name}</span>
                                  <span style={{ fontSize:11, fontWeight:700, color:RC[r] }}>
                                    {r==='accepted'?'✓ Accepted':r==='declined'?'✕ Declined':'⏳ Pending'}
                                  </span>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })()}

                    {/* Status */}
                    <div style={{ display:'flex', gap:8, marginBottom:'1.25rem', flexWrap:'wrap', alignItems:'center' }}>
                      <span style={{ fontSize:12, fontWeight:600, color:t.textMuted }}>Status:</span>
                      {Object.entries(STATUS_MAP).map(([k,v]) => (
                        <button key={k} onClick={()=>setStatus(m,k)}
                          style={{ padding:'5px 12px', borderRadius:99, fontSize:12, fontWeight:600, cursor:'pointer', border:`1px solid ${m.status===k?v.color:t.border}`, background:m.status===k?v.bg:'transparent', color:m.status===k?v.color:t.textSub, fontFamily:'Inter,system-ui' }}>
                          {v.icon} {v.label}
                        </button>
                      ))}
                      {m.status !== 'completed' && (
                        <button onClick={()=>setStatus(m,'completed')}
                          style={{ marginLeft:'auto', padding:'6px 16px', background:'linear-gradient(135deg,#059669,#10B981)', border:'none', borderRadius:8, fontSize:12, fontWeight:700, color:'#fff', cursor:'pointer', fontFamily:'Inter,system-ui' }}>
                          🔒 Close Meeting
                        </button>
                      )}
                    </div>

                    {(m.agenda || m.notes) && (
                      <div style={{ display:'grid', gridTemplateColumns:m.agenda&&m.notes?'1fr 1fr':'1fr', gap:14, marginBottom:'1.25rem' }}>
                        {m.agenda && (
                          <div style={{ background:t.dark?'rgba(255,255,255,0.04)':'#F8F9FA', borderRadius:10, padding:'10px 14px' }}>
                            <div style={{ fontSize:10, fontWeight:700, color:t.textMuted, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:6 }}>Agenda</div>
                            <p style={{ fontSize:13, color:t.text, margin:0, lineHeight:1.7 }}>{m.agenda}</p>
                          </div>
                        )}
                        {m.notes && (
                          <div style={{ background:t.dark?'rgba(255,255,255,0.04)':'#F8F9FA', borderRadius:10, padding:'10px 14px' }}>
                            <div style={{ fontSize:10, fontWeight:700, color:t.textMuted, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:6 }}>Notes</div>
                            <p style={{ fontSize:13, color:t.text, margin:0, lineHeight:1.7 }}>{m.notes}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Action items */}
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:t.text }}>
                        Action Items
                        {items.length > 0 && <span style={{ fontSize:11, fontWeight:400, color:t.textMuted, marginLeft:8 }}>{doneCount}/{items.length} done</span>}
                      </div>
                      <button onClick={()=>setQuickAdd(prev=>({...prev,[m.id]: qa ? null : true}))}
                        style={{ fontSize:12, fontWeight:600, padding:'5px 12px', background:qa?t.bg:`${t.primary}14`, border:`1px solid ${qa?t.border:`${t.primary}30`}`, borderRadius:7, color:qa?t.textSub:t.primary, cursor:'pointer', fontFamily:'Inter,system-ui' }}>
                        {qa ? '✕ Cancel' : '+ Add action item'}
                      </button>
                    </div>

                    {qa && (
                      <QuickAddItem t={t} employees={employees}
                        onSave={data=>addQuickItem(m, data)}
                        onCancel={()=>setQuickAdd(prev=>({...prev,[m.id]:null}))} />
                    )}

                    {items.length === 0 && !qa ? (
                      <div style={{ fontSize:12, color:t.textMuted, fontStyle:'italic', padding:'6px 0 10px' }}>No action items yet</div>
                    ) : items.map((item, idx) => {
                      const assignee = empMap[item.assigned_to]
                      const overdue  = !item.completed && item.due_date && isPast(item.due_date)
                      return (
                        <div key={item.id} style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'8px 0', borderBottom:`1px solid ${t.border}` }}>
                          <div onClick={()=>toggleItem(m,idx)}
                            style={{ width:20, height:20, borderRadius:5, border:`2px solid ${item.completed?'#059669':t.border}`, background:item.completed?'#059669':'transparent', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, marginTop:2 }}>
                            {item.completed && <span style={{ fontSize:10, color:'#fff', fontWeight:900 }}>✓</span>}
                          </div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, fontWeight:600, color:item.completed?t.textMuted:t.text, textDecoration:item.completed?'line-through':'none' }}>
                              {item.title}
                            </div>
                            <div style={{ display:'flex', gap:8, marginTop:3, flexWrap:'wrap' }}>
                              {assignee && <span style={{ fontSize:11, color:t.textMuted }}>👤 {assignee.full_name}</span>}
                              {item.due_date && <span style={{ fontSize:11, fontWeight:600, color:overdue?'#EF4444':t.textMuted }}>📅 {fmtDate(item.due_date)}{overdue?' — Overdue':''}</span>}
                            </div>
                            {(m.item_progress_notes || {})[item.id] && (
                              <div style={{ marginTop:6, background:'#EEF2FF', borderRadius:6, padding:'6px 10px', fontSize:12, color:'#4F46E5', lineHeight:1.5 }}>
                                💬 <strong>Progress:</strong> {(m.item_progress_notes || {})[item.id]}
                              </div>
                            )}
                          </div>
                          <div style={{ display:'flex', gap:4, flexShrink:0 }}>
                            <span style={{ fontSize:11, fontWeight:700, padding:'2px 7px', borderRadius:99, background:item.completed?'#ECFDF5':overdue?'#FEF2F2':t.bg, color:item.completed?'#059669':overdue?'#EF4444':t.textMuted }}>
                              {item.completed?'Done':overdue?'Overdue':'Pending'}
                            </span>
                            <button onClick={()=>removeItem(m,idx)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:14, color:t.textMuted, padding:'0 2px' }}>×</button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// QUICK ADD ITEM
// ════════════════════════════════════════════════════════════════════════════
function QuickAddItem({ t, employees, onSave, onCancel }) {
  const [title, setTitle]       = useState('')
  const [assignTo, setAssignTo] = useState('')
  const [dueDate, setDueDate]   = useState('')

  const IS = { display:'block', width:'100%', padding:'8px 10px', background:t.input, border:`1px solid ${t.inputBorder}`, borderRadius:7, fontSize:13, color:t.inputText, fontFamily:'Inter,system-ui', boxSizing:'border-box', outline:'none' }

  return (
    <div style={{ background:`${t.primary}08`, border:`1px solid ${t.primary}30`, borderRadius:10, padding:'12px', marginBottom:10 }}>
      <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="What needs to happen?" style={{ ...IS, marginBottom:8 }} />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
        <select value={assignTo} onChange={e=>setAssignTo(e.target.value)} style={IS}>
          <option value="">Assign to… (optional)</option>
          {employees.map(e => <option key={e.id} value={e.id}>{e.full_name}</option>)}
        </select>
        <input type="date" value={dueDate} onChange={e=>setDueDate(e.target.value)} style={IS} />
      </div>
      <div style={{ display:'flex', gap:8 }}>
        <button onClick={()=>{ if(!title.trim()) return; onSave({ title, assigned_to:assignTo, due_date:dueDate }) }}
          style={{ padding:'7px 16px', background:'linear-gradient(135deg,#4F46E5,#6366F1)', border:'none', borderRadius:7, fontSize:13, fontWeight:700, color:'#fff', cursor:'pointer', fontFamily:'Inter,system-ui' }}>
          Add
        </button>
        <button onClick={onCancel}
          style={{ padding:'7px 14px', background:'transparent', border:`1px solid ${t.border}`, borderRadius:7, fontSize:13, fontWeight:500, color:t.textSub, cursor:'pointer', fontFamily:'Inter,system-ui' }}>
          Cancel
        </button>
      </div>
    </div>
  )
}


// ════════════════════════════════════════════════════════════════════════════
// MODERN TIME PICKER — outside MeetingForm to prevent remount/cursor bug
// ════════════════════════════════════════════════════════════════════════════
function TimePicker({ value, onChange, label, t }) {
  function parse(v) {
    if (!v) return { hour: '', minute: '00', ampm: 'AM' }
    const [hStr, mStr] = v.split(':')
    const h24 = parseInt(hStr)
    const ampm = h24 >= 12 ? 'PM' : 'AM'
    const hour = h24 > 12 ? h24 - 12 : h24 === 0 ? 12 : h24
    return { hour: String(hour), minute: mStr || '00', ampm }
  }

  function build(hour, minute, ampm) {
    if (!hour) return ''
    let h = parseInt(hour)
    if (ampm === 'AM') { if (h === 12) h = 0 }
    else { if (h !== 12) h += 12 }
    return String(h).padStart(2, '0') + ':' + minute
  }

  const parsed = parse(value)

  function setHour(h)   { onChange(build(h,          parsed.minute, parsed.ampm)) }
  function setMinute(m) { onChange(build(parsed.hour, m,            parsed.ampm)) }
  function toggleAmPm() {
    const next = parsed.ampm === 'AM' ? 'PM' : 'AM'
    onChange(build(parsed.hour, parsed.minute, next))
  }

  const IS = { padding:'10px 12px', background:t.input, border:`1px solid ${t.inputBorder}`, borderRadius:8, fontSize:14, color:t.inputText, fontFamily:'Inter,system-ui', outline:'none', cursor:'pointer' }

  return (
    <div>
      {label && <div style={{ fontSize:13, fontWeight:600, color:t.text, marginBottom:8 }}>{label}</div>}
      <div style={{ display:'flex', gap:6, alignItems:'center' }}>
        <select value={parsed.hour} onChange={e=>setHour(e.target.value)} style={{ ...IS, width:70 }}>
          <option value="">--</option>
          {[1,2,3,4,5,6,7,8,9,10,11,12].map(h => (
            <option key={h} value={String(h)}>{String(h).padStart(2,'0')}</option>
          ))}
        </select>
        <span style={{ fontSize:18, fontWeight:700, color:t.textMuted }}>:</span>
        <select value={parsed.minute} onChange={e=>setMinute(e.target.value)} style={{ ...IS, width:70 }}>
          {['00','05','10','15','20','25','30','35','40','45','50','55'].map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        {/* Single toggle — click to flip AM ↔ PM */}
        <button type="button" onClick={toggleAmPm} title="Click to toggle AM/PM"
          style={{ padding:'10px 14px', borderRadius:8, fontSize:13, fontWeight:800, cursor:'pointer', border:'none',
            fontFamily:'Inter,system-ui', transition:'all .2s', minWidth:54, textAlign:'center',
            background: parsed.ampm === 'PM' ? '#4F46E5' : '#F1F5F9',
            color:      parsed.ampm === 'PM' ? '#fff'    : '#64748B',
            boxShadow:  parsed.ampm === 'PM' ? '0 2px 8px rgba(79,70,229,0.3)' : 'none',
          }}>
          {parsed.ampm}
        </button>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// MEETING FORM (Create / Edit)
// ════════════════════════════════════════════════════════════════════════════
function MeetingForm({ t, profile, employees, existing, presetDate, presetTime, onDone, onCancel }) {
  const isEdit = !!existing

  const [form, setForm] = useState({
    title:            existing?.title            || '',
    meeting_type:     existing?.meeting_type     || 'individual',
    employee_id:      existing?.employee_id      || '',
    attendee_ids:     existing?.attendee_ids     || [],
    meeting_date:     existing?.meeting_date     || presetDate || '',
    meeting_time:     existing?.meeting_time     || presetTime || '',
    meeting_end_time: existing?.meeting_end_time || '',
    agenda:           existing?.agenda           || '',
    notes:            existing?.notes            || '',
    status:           existing?.status           || 'scheduled',
  })
  const [orgHours, setOrgHours]         = useState({ start:'08:00', end:'18:00' })
  const [liveConflicts, setLiveConflicts] = useState([])
  const [actionItems, setActionItems]   = useState(
    (existing?.structured_action_items || []).map(i => ({ ...i, id: i.id || uid() }))
  )
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')

  // Load org office hours
  useEffect(() => {
    get('/org')
      .then(({ org }) => {
        if (org) setOrgHours({ start: org.office_start || '08:00', end: org.office_end || '18:00' })
      })
      .catch(() => {})
  }, [])

  // Live conflict check
  useEffect(() => {
    if (!form.meeting_date) { setLiveConflicts([]); return }
    const ids = form.meeting_type === 'individual'
      ? (form.employee_id ? [form.employee_id] : [])
      : form.attendee_ids
    if (!ids.length) { setLiveConflicts([]); return }
    get('/meetings')
      .then(({ meetings }) => {
        const sameDay = (meetings || []).filter(m => m.meeting_date === form.meeting_date && m.status !== 'completed')
        const conf = sameDay.filter(m => {
          if (isEdit && m.id === existing.id) return false
          return ids.some(id => m.employee_id === id || (m.attendee_ids || []).includes(id))
        })
        setLiveConflicts(conf)
      })
      .catch(() => {})
  }, [form.meeting_date, form.employee_id, form.attendee_ids, form.meeting_type])

  function handle(field, value) { setForm(prev => ({ ...prev, [field]: value })) }

  function toggleAttendee(id) {
    setForm(prev => {
      const has = prev.attendee_ids.includes(id)
      return { ...prev, attendee_ids: has ? prev.attendee_ids.filter(x=>x!==id) : [...prev.attendee_ids, id] }
    })
  }

  function addItem()           { setActionItems(prev => [...prev, emptyItem()]) }
  function removeItemById(id)  { setActionItems(prev => prev.filter(i => i.id !== id)) }
  function updateItem(id, field, val) {
    setActionItems(prev => prev.map(i => i.id===id ? { ...i, [field]: val } : i))
  }

  async function handleSave() {
    setError('')
    if (!form.title.trim())   return setError('Please enter a meeting title')
    if (!form.meeting_date)   return setError('Please select a date')
    if (form.meeting_date < new Date().toISOString().split('T')[0]) return setError('Cannot schedule a meeting in the past')
    if (form.meeting_time && (form.meeting_time < orgHours.start || form.meeting_time > orgHours.end))
      return setError(`Please select a time within office hours: ${orgHours.start} – ${orgHours.end}`)
    if (form.meeting_end_time && form.meeting_time && form.meeting_end_time <= form.meeting_time)
      return setError('End time must be after start time')
    if (form.meeting_type === 'individual' && !form.employee_id) return setError('Please select an employee')
    if (form.meeting_type === 'group' && form.attendee_ids.length < 2) return setError('Please select at least 2 attendees')

    // Time overlap conflict check (covers employees AND employer's own schedule)
    let sameDayMtgs = []
    try {
      const { meetings } = await get('/meetings')
      sameDayMtgs = (meetings || []).filter(m => m.meeting_date === form.meeting_date && m.status !== 'completed')
    } catch { /* ignore */ }

    const newStart = form.meeting_time     || '00:00'
    const newEnd   = form.meeting_end_time || '23:59'

    function timesOverlap(exStart, exEnd) {
      const s = exStart || '00:00'
      const e = exEnd   || '23:59'
      return newStart < e && newEnd > s
    }

    const ids = form.meeting_type === 'individual' ? [form.employee_id] : form.attendee_ids

    const conflicts = (sameDayMtgs || []).filter(m => {
      if (isEdit && m.id === existing.id) return false
      if (!timesOverlap(m.meeting_time, m.meeting_end_time)) return false
      // Check if employer's schedule conflicts (any overlapping meeting = employer conflict)
      // OR if employee/attendee is double-booked
      const attendeeOverlap = ids.some(id => m.employee_id === id || (m.attendee_ids || []).includes(id))
      return attendeeOverlap || true // employer attends all their meetings
    }).filter(m => {
      if (isEdit && m.id === existing.id) return false
      return timesOverlap(m.meeting_time, m.meeting_end_time)
    })

    if (conflicts.length > 0) {
      const c = conflicts[0]
      const timeInfo = c.meeting_time
        ? ` at ${fmtTime(c.meeting_time)}${c.meeting_end_time ? ' – ' + fmtTime(c.meeting_end_time) : ''}`
        : ''
      return setError(`⚠️ You already have "${c.title}"${timeInfo} scheduled at this time. Please choose a different time.`)
    }

    setSaving(true)
    const payload = {
      employee_id:             form.meeting_type === 'individual' ? form.employee_id : (form.attendee_ids[0] || null),
      title:                   form.title,
      meeting_type:            form.meeting_type,
      meeting_date:            form.meeting_date,
      meeting_time:            form.meeting_time     || null,
      meeting_end_time:        form.meeting_end_time || null,
      agenda:                  form.agenda  || null,
      notes:                   form.notes   || null,
      status:                  form.status,
      attendee_ids:            form.meeting_type === 'group' ? form.attendee_ids : [],
      structured_action_items: actionItems.filter(i => i.title.trim()),
    }

    try {
      if (isEdit) await patch(`/meetings/${existing.id}`, payload)
      else await post('/meetings', payload)
    } catch (err) { setError(err.message); setSaving(false); return }
    setSaving(false)
    onDone()
  }

  const IS = { display:'block', width:'100%', padding:'11px 14px', background:t.input, border:`1px solid ${t.inputBorder}`, borderRadius:8, fontSize:14, color:t.inputText, fontFamily:'Inter,system-ui', boxSizing:'border-box', outline:'none' }
  const LS = { display:'block', fontSize:13, fontWeight:600, color:t.text, marginBottom:6 }

  return (
    <div>
      <button onClick={onCancel} style={{ fontSize:13, color:t.textMuted, background:'none', border:'none', cursor:'pointer', marginBottom:'1.25rem', fontFamily:'Inter,system-ui' }}>
        ← Back to meetings
      </button>
      <div style={{ marginBottom:'1.75rem' }}>
        <h1 style={{ fontSize:24, fontWeight:800, color:t.text, margin:'0 0 4px', letterSpacing:'-0.5px' }}>
          {isEdit ? '✏️ Edit Meeting' : 'Schedule Meeting'}
        </h1>
        <p style={{ fontSize:14, color:t.textMuted, margin:0 }}>
          {isEdit ? 'Update details, add notes, or edit action items' : 'Create a meeting and pre-plan action items'}
        </p>
      </div>

      <div style={{ width: '100%' }}>

        {/* Title + type + date/time */}
        <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.5rem', marginBottom:16, boxShadow:t.shadow }}>
          <div style={{ marginBottom:'1.25rem' }}>
            <label style={{ ...LS, fontSize:14 }}>Meeting Title *</label>
            <input value={form.title} onChange={e=>handle('title',e.target.value)}
              placeholder="e.g. Q2 Performance Check-in…"
              style={{ ...IS, fontSize:18, fontWeight:700, padding:'13px 16px' }} />
          </div>

          <div style={{ marginBottom:'1.25rem' }}>
            <label style={LS}>Meeting Type *</label>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {[['individual','👤 Individual','One-on-one'],['group','👥 Group','Multiple attendees']].map(([v,l,sub]) => (
                <div key={v} onClick={()=>handle('meeting_type',v)}
                  style={{ padding:'12px', borderRadius:10, border:`2px solid ${form.meeting_type===v?'#4F46E5':t.border}`, background:form.meeting_type===v?'#4F46E508':t.card, cursor:'pointer' }}>
                  <div style={{ fontSize:14, fontWeight:700, color:form.meeting_type===v?'#4F46E5':t.text }}>{l}</div>
                  <div style={{ fontSize:11, color:t.textMuted, marginTop:2 }}>{sub}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14 }}>
            <div>
              <label style={LS}>Date *</label>
              <input type="date" value={form.meeting_date}
                min={new Date().toISOString().split('T')[0]}
                onChange={e=>handle('meeting_date',e.target.value)} style={IS} />
              <div style={{ fontSize:11, color:t.textMuted, marginTop:3 }}>Cannot book past dates</div>
            </div>
            <div>
              <TimePicker t={t}
                label={<>From <span style={{ fontSize:11, fontWeight:400, color:t.textMuted }}>(office: {orgHours.start}–{orgHours.end})</span></>}
                value={form.meeting_time}
                onChange={v=>{ setError(''); handle('meeting_time', v) }} />
            </div>
            <div>
              <TimePicker t={t}
                label="To"
                value={form.meeting_end_time}
                onChange={v=>{ setError(''); handle('meeting_end_time', v) }} />
            </div>
          </div>

          <div style={{ marginTop:14 }}>
            <label style={LS}>Status</label>
            <select value={form.status} onChange={e=>handle('status',e.target.value)} style={{ ...IS, cursor:'pointer', maxWidth:220 }}>
              <option value="scheduled">📅 Scheduled</option>
              <option value="in_progress">🗣️ In Progress</option>
              <option value="completed">✅ Completed</option>
            </select>
          </div>

          {/* Live conflict warning */}
          {liveConflicts.length > 0 && (
            <div style={{ marginTop:14, background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'12px 14px' }}>
              <div style={{ fontSize:13, fontWeight:700, color:'#DC2626', marginBottom:8 }}>⚠️ Conflict detected on {form.meeting_date}</div>
              {liveConflicts.map(c => (
                <div key={c.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0', borderTop:'1px solid #FECACA' }}>
                  <span style={{ fontSize:16 }}>🗓</span>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:'#DC2626' }}>{c.title}</div>
                    <div style={{ fontSize:11, color:'#B91C1C' }}>
                      {c.meeting_time && `${fmtTime(c.meeting_time)}${c.meeting_end_time ? ' – ' + fmtTime(c.meeting_end_time) : ''}`}
                      {' · '}{c.meeting_type === 'group' ? 'Group meeting' : 'Individual meeting'}
                    </div>
                  </div>
                </div>
              ))}
              <div style={{ fontSize:12, color:'#B91C1C', marginTop:8 }}>
                This attendee already has a meeting on this date. Saving will double-book them.
              </div>
            </div>
          )}
        </div>

        {/* Attendees */}
        <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.5rem', marginBottom:16, boxShadow:t.shadow }}>
          <label style={LS}>{form.meeting_type==='group'?'Attendees *':'Employee *'}</label>
          {form.meeting_type === 'individual' ? (
            <select value={form.employee_id} onChange={e=>handle('employee_id',e.target.value)} style={{ ...IS, cursor:'pointer' }}>
              <option value="">— Choose an employee —</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.full_name}{e.position?` · ${e.position}`:''}</option>)}
            </select>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:8 }}>
              {employees.map((e,i) => {
                const sel = form.attendee_ids.includes(e.id)
                return (
                  <div key={e.id} onClick={()=>toggleAttendee(e.id)}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:10, border:`2px solid ${sel?gc(i):t.border}`, background:sel?`${gc(i)}10`:t.card, cursor:'pointer' }}>
                    <div style={{ width:28, height:28, borderRadius:'50%', background:sel?gc(i):t.border, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:800, color:'#fff', flexShrink:0 }}>
                      {e.full_name?.charAt(0)}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:sel?gc(i):t.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.full_name}</div>
                      {e.position && <div style={{ fontSize:11, color:t.textMuted }}>{e.position}</div>}
                    </div>
                    {sel && <span style={{ fontSize:13, color:gc(i) }}>✓</span>}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Agenda + notes */}
        <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.5rem', marginBottom:16, boxShadow:t.shadow }}>
          <div style={{ marginBottom:'1.25rem' }}>
            <label style={LS}>Agenda</label>
            <textarea value={form.agenda} onChange={e=>handle('agenda',e.target.value)}
              placeholder="Topics to discuss…" rows={3} style={{ ...IS, resize:'vertical' }} />
          </div>
          <div>
            <label style={LS}>Meeting Notes {isEdit && <span style={{ fontSize:11, color:'#059669', fontWeight:400 }}>← add during or after meeting</span>}</label>
            <textarea value={form.notes} onChange={e=>handle('notes',e.target.value)}
              placeholder="Key decisions, feedback given, outcomes…" rows={3} style={{ ...IS, resize:'vertical' }} />
          </div>
        </div>

        {/* Action items */}
        <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.5rem', marginBottom:20, boxShadow:t.shadow }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
            <div>
              <div style={{ fontSize:14, fontWeight:700, color:t.text, marginBottom:2 }}>Action Items</div>
              <div style={{ fontSize:12, color:t.textMuted }}>Tasks to complete before, during, or after this meeting</div>
            </div>
            <button onClick={addItem}
              style={{ padding:'7px 14px', background:`${t.primary}14`, border:`1px solid ${t.primary}30`, borderRadius:8, fontSize:13, fontWeight:600, color:t.primary, cursor:'pointer', fontFamily:'Inter,system-ui' }}>
              + Add item
            </button>
          </div>

          {actionItems.length === 0 ? (
            <div style={{ textAlign:'center', padding:'1.25rem', border:`2px dashed ${t.border}`, borderRadius:10, color:t.textMuted, fontSize:13 }}>
              No action items yet — click "+ Add item"
            </div>
          ) : actionItems.map(item => (
            <div key={item.id} style={{ background:t.dark?'rgba(255,255,255,0.03)':'#F8F9FA', border:`1px solid ${t.border}`, borderRadius:10, padding:'12px', marginBottom:8 }}>
              <div style={{ display:'flex', gap:8, marginBottom:8, alignItems:'center' }}>
                <input value={item.title} onChange={e=>updateItem(item.id,'title',e.target.value)}
                  placeholder="What needs to happen?"
                  style={{ ...IS, flex:1, padding:'8px 10px', fontSize:13 }} />
                <button onClick={()=>removeItemById(item.id)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:18, color:t.textMuted, flexShrink:0 }}>×</button>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                <select value={item.assigned_to} onChange={e=>updateItem(item.id,'assigned_to',e.target.value)} style={{ ...IS, fontSize:12, padding:'7px 10px' }}>
                  <option value="">Assign to… (optional)</option>
                  {employees.map(e=><option key={e.id} value={e.id}>{e.full_name}</option>)}
                </select>
                <input type="date" value={item.due_date} onChange={e=>updateItem(item.id,'due_date',e.target.value)} style={{ ...IS, fontSize:12, padding:'7px 10px' }} />
              </div>
            </div>
          ))}
        </div>

        {error && (
          <div style={{ fontSize:13, color:'#EF4444', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'10px 14px', marginBottom:14 }}>⚠️ {error}</div>
        )}

        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', paddingTop:'1rem', borderTop:`1px solid ${t.border}` }}>
          <button onClick={onCancel} style={{ padding:'11px 22px', background:t.card, border:`1px solid ${t.border}`, borderRadius:8, fontSize:14, fontWeight:500, color:t.textSub, cursor:'pointer', fontFamily:'Inter,system-ui' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding:'11px 28px', background:saving?t.border:'linear-gradient(135deg,#4F46E5,#6366F1)', border:'none', borderRadius:8, fontSize:14, fontWeight:700, color:saving?t.textMuted:'#fff', cursor:saving?'not-allowed':'pointer', fontFamily:'Inter,system-ui', boxShadow:saving?'none':'0 2px 8px rgba(79,70,229,0.3)' }}>
            {saving ? 'Saving…' : isEdit ? '✅ Save Changes' : '✅ Create Meeting'}
          </button>
        </div>
      </div>
    </div>
  )
}
